import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Brain, Users, ClipboardList, Target, HelpCircle, Trophy, Phone, Star } from "lucide-react";
import { BRAND, IMG, telLink, waLink } from "@/lib/brand";
import InstagramSection from "@/components/site/InstagramSection";

const features = [
  { icon: Brain, title: "Concept-Based Learning", body: "We build strong fundamentals so students never fear exams — they own them.", span: "md:col-span-2" },
  { icon: Users, title: "Small Batch Size", body: "Limited seats per batch so every child gets personal attention.", span: "md:col-span-1" },
  { icon: ClipboardList, title: "Weekly Tests", body: "Regular assessments to track progress and fix weak areas early.", span: "md:col-span-1" },
  { icon: Target, title: "Personal Mentoring", body: "One-on-one guidance from our teachers to keep students motivated.", span: "md:col-span-2" },
  { icon: HelpCircle, title: "Doubt-Solving Sessions", body: "Dedicated doubt classes — no question left unanswered.", span: "md:col-span-1" },
  { icon: Trophy, title: "Board-Focused Prep", body: "CBSE & ICSE pattern practice with NCERT + previous year papers.", span: "md:col-span-2" },
];

const courses = [
  { badge: "Best Value", featured: true, title: "Complete All-Subjects Package", subjects: "Every subject · every class · tests + notes + doubts", duration: "Full academic session", fee: "₹2,500 / month · all subjects", cta: "Enroll Now", href: "/courses#all-subjects" },
  { badge: "Class VI – VIII", title: "Foundation Batch", subjects: "Maths · Science · English · Social · Hindi · Computer", duration: "Full academic year", fee: "₹999 / subject / month", cta: "Enroll Now", href: "/courses" },
  { badge: "Class IX", title: "Full Course", subjects: "Maths · Physics · Chemistry · Biology · English · Social", duration: "12 months", fee: "₹999 / subject / month", cta: "Enroll Now", href: "/courses" },
  { badge: "Class X", title: "Board Batch (CBSE / ICSE)", subjects: "All main subjects with board-focused practice", duration: "10 months (Apr–Feb)", fee: "₹999 / subject / month", cta: "Join Today", href: "/courses" },
  { badge: "Crash Course", title: "Revision Batch", subjects: "Chapter-wise revision, PYQs, mock tests", duration: "8–10 weeks", fee: "Contact for fees", cta: "Book Free Demo", href: "/contact" },
];

const stats = [
  { value: "95%+", label: "Highest Marks Scored" },
  { value: "500+", label: "Students Mentored" },
  { value: "100%", label: "Board Pass Rate" },
  { value: "50+", label: "School Toppers" },
];

const previewTestimonials = [
  { q: "My daughter went from 68% to 92% in one year. The teachers' attention to each child is unmatched.", by: "Mrs. Sunita Kumari", role: "Parent, Class X" },
  { q: "I used to hate Maths. Now it's my favourite subject — thanks to my Vikramshila teachers.", by: "Aarav K.", role: "Class X CBSE" },
  { q: "Weekly tests keep my son disciplined. Best decision we made for his studies.", by: "Mr. Rakesh Sharma", role: "Parent, Class IX" },
];

const previewFaq = [
  { q: "What are the fees at Vikramshila Classes?", a: "Our standard fee is ₹999 per subject per month. We also offer a special all-subjects bundle at ₹2,500 per month — great value if your child takes 3+ subjects. Crash courses are priced separately." },
  { q: "Are classes online or offline?", a: "Primarily offline at our Rajeev Nagar centre. Recorded revision support is included." },
  { q: "How often are tests conducted?", a: "Weekly chapter tests, monthly cumulative tests, and pre-board mocks for Class X." },
  { q: "Is there an entrance test for admission?", a: "No. Book a free demo, meet our teachers, and if it's a fit — enroll." },
];

export default function HomePage() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B192C] text-white">
        <div className="vk-grain" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="vk-chip" data-testid="hero-chip">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B35]" /> Admissions open · Class VI – X · CBSE &amp; ICSE
            </span>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-800 leading-[1.02] tracking-tight">
              Shaping Futures.<br />
              <span className="text-[#FF6B35]">Building Excellence.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/70 leading-relaxed">
              Patna&apos;s trusted coaching for Class VI to X — all subjects, small batches, weekly tests, and mentoring
              that turns average students into confident toppers.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={waLink()} target="_blank" rel="noreferrer" data-testid="hero-demo-cta" className="vk-btn-primary">
                Book Free Demo Class <ArrowRight size={18} />
              </a>
              <a href={waLink("Hello, I want to know more about Vikramshila Classes.")} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-cta" className="vk-btn-outline">
                WhatsApp Now
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 max-w-md gap-6">
              {[{k:"12+",v:"Years teaching"},{k:"500+",v:"Students"},{k:"50+",v:"Toppers"}].map((s)=>(
                <div key={s.k}>
                  <div className="text-3xl font-display font-800 text-[#FF6B35]">{s.k}</div>
                  <div className="text-xs uppercase tracking-widest text-white/50 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-lg overflow-hidden border border-white/10">
              <img src="https://customer-assets.emergentagent.com/job_vikram-patna/artifacts/57uqp6iu_IMG-20260710-WA0012.jpg" alt="Vikramshila Classes centre in Rajeev Nagar, Patna — CBSE and ICSE coaching for Class VI to X" className="w-full h-full object-cover" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0B192C]/70 via-transparent to-transparent" />
            </div>
            <div className="hidden md:block absolute -bottom-6 -left-6 bg-white text-[#0F172A] rounded-lg shadow-xl p-5 max-w-[240px] border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                {[...Array(5)].map((_,i)=>(<Star key={i} size={14} className="text-[#FF6B35] fill-[#FF6B35]" />))}
              </div>
              <div className="text-sm font-medium leading-snug">&quot;Every concept felt crystal clear.&quot;</div>
              <div className="text-xs text-slate-500 mt-2">— Aarav, 98% Class X CBSE</div>
            </div>
            <div className="hidden lg:flex absolute -top-4 -right-4 bg-[#FF6B35] text-white rounded-lg px-4 py-3 items-center gap-2 shadow-lg">
              <Trophy size={18} />
              <div>
                <div className="text-xs uppercase tracking-widest opacity-90">Board 2025</div>
                <div className="font-display font-bold">50+ Toppers</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <div data-testid="trust-bar" className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-700">
          {["CBSE & ICSE","Classes VI–X","All Subjects","Weekly Tests","Small Batches","Board-Focused Prep"].map((t)=>(
            <div key={t} className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-[#FF6B35]" /> <span className="font-medium">{t}</span>
            </div>
          ))}
        </div>
      </div>

      {/* WHY CHOOSE US */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="vk-chip">Why us</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-800 tracking-tight text-[#0B192C]">Why Parents &amp; Students Choose Us</h2>
            <p className="mt-4 text-slate-600 text-lg">We don&apos;t just teach — we mentor. Every student gets the attention, discipline and structure they need to top their class.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {features.map(({icon:Icon,title,body,span},i)=>(
              <div key={title} data-testid={`why-card-${i}`} className={`group ${span} rounded-lg border border-slate-200 bg-white p-8 hover:border-[#FF6B35] transition-colors`}>
                <div className="w-12 h-12 rounded-md bg-[#0B192C] text-[#FF6B35] grid place-items-center mb-6">
                  <Icon size={22} />
                </div>
                <div className="font-display text-xl font-bold text-[#0B192C]">{title}</div>
                <p className="mt-2 text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COURSES */}
      <section className="py-20 lg:py-24 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <span className="vk-chip">Our courses</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-800 tracking-tight text-[#0B192C]">Complete Coverage. Every Subject.</h2>
              <p className="mt-3 max-w-xl text-slate-600 text-lg">Class VI to X · CBSE &amp; ICSE — from foundation to board mastery.</p>
            </div>
            <Link to="/courses" data-testid="see-all-courses" className="vk-btn-dark text-sm">See all courses <ArrowRight size={16} /></Link>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {courses.map((c,i)=>(
              <div key={c.title} data-testid={`course-card-${i}`} className={`rounded-lg bg-white p-6 flex flex-col transition-shadow hover:shadow-md ${c.featured ? "border-2 border-[#FF6B35] shadow-lg shadow-[#FF6B35]/10 relative" : "border border-slate-200"}`}>
                {c.featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-widest bg-[#FF6B35] text-white px-3 py-1 rounded-full">Most Popular</span>}
                <span className="vk-chip self-start">{c.badge}</span>
                <div className="font-display text-2xl font-bold text-[#0B192C] mt-4">{c.title}</div>
                <div className="mt-2 text-sm text-slate-600 flex-1">{c.subjects}</div>
                <div className="mt-4 text-xs uppercase tracking-widest text-slate-500">Duration</div>
                <div className="text-sm text-[#0F172A] font-medium">{c.duration}</div>
                <div className="mt-3 text-xs uppercase tracking-widest text-slate-500">Fee</div>
                <div className="text-lg font-display font-bold text-[#FF6B35]">{c.fee}</div>
                <Link to={c.href} className="vk-btn-primary mt-5 justify-center text-sm">{c.cta} <ArrowRight size={16} /></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="py-20 lg:py-24 bg-[#0B192C] text-white relative overflow-hidden">
        <div className="vk-grain" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="vk-chip">Results</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-800 tracking-tight">Results That Make Us Proud</h2>
            <p className="mt-4 text-white/70 text-lg">Every year, our students prove what focused mentoring can do.</p>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {stats.map((s)=>(
              <div key={s.label} className="rounded-lg bg-white/5 border border-white/10 p-8">
                <div className="font-display text-5xl font-800 text-[#FF6B35]">{s.value}</div>
                <div className="mt-2 text-sm uppercase tracking-widest text-white/60">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/results" data-testid="see-achievers" className="vk-btn-outline">See all achievers <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS PREVIEW */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <span className="vk-chip">Testimonials</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-800 tracking-tight text-[#0B192C]">What Parents &amp; Students Say</h2>
            </div>
            <Link to="/testimonials" className="vk-btn-dark text-sm">Read more reviews <ArrowRight size={16} /></Link>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {previewTestimonials.map((t,i)=>(
              <div key={i} data-testid={`home-testimonial-${i}`} className="rounded-lg border border-slate-200 p-8 bg-[#F5F7FA]">
                <div className="flex gap-1">{[...Array(5)].map((_,j)=>(<Star key={j} size={14} className="text-[#FF6B35] fill-[#FF6B35]" />))}</div>
                <p className="mt-4 text-slate-800 leading-relaxed">&ldquo;{t.q}&rdquo;</p>
                <div className="mt-6 text-sm font-semibold text-[#0B192C]">{t.by}</div>
                <div className="text-xs text-slate-500">{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT INSTITUTE */}
      <section className="py-20 lg:py-24 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative aspect-[4/5] max-w-md rounded-lg overflow-hidden border border-slate-200">
            <img src={IMG.teacher} alt="Vikramshila Classes teacher explaining a concept to students in Patna" className="w-full h-full object-cover" />
          </div>
          <div>
            <span className="vk-chip">About Vikramshila Classes</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-800 tracking-tight text-[#0B192C]">Teachers who care. A method that works.</h2>
            <p className="mt-5 text-slate-700 text-lg leading-relaxed">
              Vikramshila Classes is a Patna-based coaching centre for Class VI to X. Our faculty combines subject
              mastery with genuine mentoring — the kind that turns confused students into confident toppers.
            </p>
            <blockquote className="mt-6 border-l-4 border-[#FF6B35] pl-5 italic text-slate-800">
              &ldquo;Understand the concept, and marks will follow.&rdquo;
            </blockquote>
            <Link to="/about" data-testid="read-teacher-story" className="vk-btn-dark mt-8 text-sm">Learn more about us <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="vk-chip">Quick answers</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-800 tracking-tight text-[#0B192C]">Frequently Asked</h2>
          </div>
          <div className="mt-10 divide-y divide-slate-200 border border-slate-200 rounded-lg">
            {previewFaq.map((f,i)=>(
              <details key={i} className="group p-6" data-testid={`home-faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between font-semibold text-[#0B192C]">
                  {f.q}
                  <span className="text-[#FF6B35] group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/faq" className="vk-btn-dark text-sm">See all FAQs <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <InstagramSection />

      {/* FINAL CTA */}
      <section className="py-20 lg:py-24 bg-[#0B192C] text-white relative overflow-hidden">
        <div className="vk-grain" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-800 tracking-tight">Ready to Join {BRAND.fullName}?</h2>
          <p className="mt-4 text-white/70 text-lg">Book your free demo today and see the difference in your child&apos;s learning.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact" data-testid="final-demo-cta" className="vk-btn-primary">Book Free Demo Class <ArrowRight size={18} /></Link>
            <a href={waLink()} target="_blank" rel="noreferrer" className="vk-btn-outline">WhatsApp Now</a>
            <a href={telLink} className="vk-btn-outline"><Phone size={16} /> {BRAND.phonePrimary}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
